import { Observable, type Subscriber, type Subscription, type TeardownLogic } from 'rxjs';

const noValue = Symbol('noValue');

interface SubscriberFn<T> {
  next(value: T): void;

  error(err: any): void;
}

/**
 * A cache for request-like Observables that:
 * - Lazily requests the value on first subscription.
 * - Caches the latest successfully emitted value and immediately re-emits it to new subscribers.
 * - Deduplicates concurrent requests (only one in-flight request at a time).
 * - Never completes the observers even when the source completes.
 * - On `clear()`, drops the cached value and, if there are active subscribers, re-requests immediately.
 *
 * @typeParam T - The value type produced by the source observable.
 */
export default class RequestCache<T> {
  public readonly observable: Observable<T> = new Observable<T>(this.handleNewSubscriber.bind(this));
  private readonly activeSubscribers: Set<SubscriberFn<T>> = new Set();

  private lastValue: T | typeof noValue = noValue;
  private lastError: any | typeof noValue = noValue;
  private currentSubscription: Subscription | null = null;

  public constructor(private readonly source: Observable<T>,
                     private readonly debugLabel?: string) {
  }

  public clearCache(): void {
    if (this.debugLabel) console.log('[%s] Clearing cache', this.debugLabel);
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }

    this.lastValue = noValue;
    this.lastError = noValue;

    if (this.activeSubscribers.size > 0) {
      this.fetchValue();
    }
  }

  public updateValue(operator: (current: T) => T): void {
    if (this.lastValue !== noValue) {
      this.lastValue = operator(this.lastValue);
      this.activeSubscribers.forEach(subscriber => subscriber.next(this.lastValue as T));
    }
  }

  public setValue(value: T): void {
    this.lastValue = value;
    this.lastError = noValue;
    this.activeSubscribers.forEach(subscriber => subscriber.next(value));
  }

  public getOnce(): Promise<T> {
    if (this.lastValue !== noValue) {
      return Promise.resolve(this.lastValue);
    }

    if (this.lastError !== noValue) {
      return Promise.reject(this.lastError);
    }

    return new Promise<T>((resolve, reject) => {
      const subscriber: SubscriberFn<T> = {
        next: value => {
          resolve(value);
          this.onUnsubscribe(subscriber);
        },
        error: err => {
          reject(err);
          this.onUnsubscribe(subscriber);
        },
      };
      this.activeSubscribers.add(subscriber);
      this.fetchValue();
    });
  }

  private handleNewSubscriber(subscriber: Subscriber<T>): TeardownLogic {
    if (this.debugLabel) console.log('[%s] New subscriber', this.debugLabel);
    this.activeSubscribers.add(subscriber);

    if (this.lastValue !== noValue) {
      subscriber.next(this.lastValue);
    } else if (this.lastError !== noValue) {
      subscriber.error(this.lastError);
    } else {
      this.fetchValue();
    }

    return this.onUnsubscribe.bind(this, subscriber);
  }

  private fetchValue(): void {
    if (this.currentSubscription) {
      return;
    }

    if (this.debugLabel) console.log('[%s] Fetching value', this.debugLabel);
    this.currentSubscription = this.source.subscribe({
      next: value => {
        if (this.debugLabel) console.log('[%s] Received value', this.debugLabel);
        this.lastValue = value;
        this.lastError = noValue;
        this.activeSubscribers.forEach(subscriber => subscriber.next(value));
      },
      error: err => {
        if (this.debugLabel) console.log('[%s] Received error', this.debugLabel);
        this.lastValue = noValue;
        this.lastError = err;
        this.activeSubscribers.forEach(subscriber => subscriber.error(err));
      },
      complete: () => {
        if (this.debugLabel) console.log('[%s] Source completed', this.debugLabel);
        this.currentSubscription?.unsubscribe();
        this.currentSubscription = null;
      },
    });
  }

  private onUnsubscribe(subscriber: SubscriberFn<T>): void {
    this.activeSubscribers.delete(subscriber);
    if (this.activeSubscribers.size === 0 && this.currentSubscription) {
      if (this.debugLabel) console.log('[%s] No active subscribers, cancelling in-flight request', this.debugLabel);
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
  }
}
